import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
// NOTE: the previous landing page (./App) is intentionally left imported-out —
// the file is kept in the repo but is no longer wired to any route, so it is
// unreachable from the front. The campus pathfinder is now the site root.
import PathfinderPage from './PathfinderPage';
import StuClarkPage from './StuClarkPage';
import EITCPage from './EITCPage';
import ExamsPage from './ExamsPage';
import ParkingCreator from './ParkingCreator';
import ParkingPage from './ParkingPage';
import ParkConnectPage from './ParkConnectPage';
import ParkConnectAdmin from './ParkConnectAdmin';
import BuildingDirectoriesPage from './BuildingDirectoriesPage';
import EventCreatorPage from './EventCreatorPage';
import EventsPage from './EventsPage';
import ShuttleTrackerPage from './shuttle/TrackerPage';
// TunnelsPage is intentionally imported-out, like ./App above: /tunnels now
// redirects to /umanitoba (vercel.json), since the campus pathfinder answers
// the tunnel question itself. The file is kept, just not wired to a route.
import EngTourPage from './EngTourPage';
import EngTourCreator from './EngTourCreator';
import WelcomeDayPage from './WelcomeDayPage';
import WelcomeDayEditorPage from './WelcomeDayEditorPage';
import AdminPage from './AdminPage';
import PhotoTrackerPage from './PhotoTrackerPage';
import LandingPage from './LandingPage';
import MobilePrivacyPage from './MobilePrivacyPage';
import MobilePage from './MobilePage';
import ChangelogPage from './ChangelogPage';
import VolunteerPage from './VolunteerPage';
import { inject } from '@vercel/analytics';
import reportWebVitals from './reportWebVitals';

// The scavenger hunt is one event's pages, so their code (and CSS) is split
// out and fetched only on those two paths rather than by every UMaps visitor.
const NBSCHuntPage = React.lazy(() => import('./NBSCHuntPage'));
const NBSCHuntAdminPage = React.lazy(() => import('./NBSCHuntAdminPage'));

// ⚠️ Sign-in links carry a one-use secret in the query: `?setup=` (a club's
// link to /eventmapcreator) and `?signin=` (a volunteer's to /volunteer). Each
// page strips it from the address bar once it is spent — but the pageview is
// recorded on load, before that, and Vercel Web Analytics sends the full URL.
// Without this every live sign-in link would sit in the analytics dashboard.
// `join` is a scavenger-hunt team code from a team's QR (NBSCHuntPage).
const SECRET_PARAMS = ['setup', 'signin', 'token', 'join'];
inject({
  beforeSend: (event) => {
    try {
      const url = new URL(event.url);
      let changed = false;
      for (const k of SECRET_PARAMS) {
        if (url.searchParams.has(k)) { url.searchParams.delete(k); changed = true; }
      }
      return changed ? { ...event, url: url.toString() } : event;
    } catch {
      return event;
    }
  },
});

const path = window.location.pathname.replace(/\/$/, '');

// ⚠️ /eventmap IS RETIRED, AND ITS QR CODES ARE NOT. That page — every event on
// a map of its own — is now what the campus map at /umanitoba does, with pins
// and a calendar, so keeping a second one meant two screens answering "what's
// on" and drifting apart. `EventMapPage.js` is deleted, not parked.
//
// But the whole point of an event map was the printed QR, and a poster on a
// wall cannot be edited: links carrying `?e=<slug>` or `?id=` are still being
// scanned. So the QUERY STRING SURVIVES THE REDIRECT — dropping it would send
// somebody who scanned a specific event to a general map and leave them to find
// it, which is the failure the QR existed to prevent. `window.location.replace`
// rather than `assign` or a rendered redirect, so the dead path leaves no
// history entry: the back button from /umanitoba must reach wherever they came
// from, not bounce them through here again.
//
// This runs before React renders anything at all. A redirect that waits for a
// component to mount shows a flash of the wrong page first.
if (path === '/eventmap') {
  window.location.replace(`/umanitoba${window.location.search}${window.location.hash}`);
}

// The scavenger hunt was "ASBC" for its first day live (2026-11-03) before the
// event was renamed NBSC; a link shared in that window still lands.
const OLD_HUNT = { '/asbcscavengerhunt': '/NBSCScavengerhunt', '/asbcscavengerhuntadmin': '/NBSCScavengerhuntadmin' };
const oldHunt = OLD_HUNT[path.toLowerCase()];
if (oldHunt) window.location.replace(`${oldHunt}${window.location.search}${window.location.hash}`);

/** Nothing, for the moment between the redirect above and the browser leaving. */
const Redirecting = () => null;

const Page = path === '/stuclark' ? StuClarkPage
           : path === '/eitc' ? EITCPage
           : path === '/exams' ? ExamsPage
           : path === '/parkingcreator' ? ParkingCreator
           : path === '/parking' ? ParkingPage
           : path === '/parkconnect' ? ParkConnectPage // permit sharing board
           : path === '/parkconnectadmin' ? ParkConnectAdmin // its moderation queue
           : path === '/buildingdirectories' ? BuildingDirectoriesPage // faculty QR signage
           : path === '/eventmapcreator' ? EventCreatorPage // club event dashboard
           : path === '/events' ? EventsPage // one event, with directions
           // The redirect above is already under way; this draws nothing while
           // the browser gets there. Falling through to the landing page would
           // flash a support CTA at somebody who scanned an event's QR code.
           : path === '/eventmap' || oldHunt ? Redirecting
           // The redirect above is server-side; this keeps /tunnels landing on
           // the pathfinder in local dev and on any client-side navigation too.
           : path === '/tunnels' ? PathfinderPage
           : path === '/engtour' ? EngTourPage
           : path === '/engtourcreator' ? EngTourCreator
           : path === '/welcomeday' ? WelcomeDayPage
           : path === '/welcomedayeditor' ? WelcomeDayEditorPage
           : path === '/admin' ? AdminPage
           // The phone riding the campus shuttle — password + emailed code, then
           // a Start button. See src/shuttle/TrackerPage.js.
           : path === '/shuttle/track' ? ShuttleTrackerPage
           // Volunteer Tools. One component for /volunteer and its three tools
           // (/volunteer/moderate, /volunteer/issues, /volunteer/content) — the
           // page reads the rest of the path itself, so moving between tools
           // keeps the session and does not reload. /admin links to these four
           // exact URLs; they must not move.
           : path === '/volunteer' || path.startsWith('/volunteer/') ? VolunteerPage
           : path === '/phototracker' ? PhotoTrackerPage // password-gated field tool
           : path === '/intersections' ? PhotoTrackerPage // former name, kept working
           : path === '/mobile-privacy-policy' ? MobilePrivacyPage // linked from both app store listings
           : path === '/mobile' ? MobilePage // the app's landing page: the two store links
           // What changed in the app, with the student feedback that prompted
           // it. Above the /mobile arms rather than below only for reading
           // order — every arm here is an exact match, so /mobile/changelog
           // could never have been swallowed by /mobile.
           : path === '/mobile/changelog' ? ChangelogPage
           // Referral links from inside the app: /mobile/refer/<code>, and the
           // codeless /mobile/rf that a phone falls back to. Both are the same
           // page — the code is read from the path by MobilePage, not routed on,
           // because a referral is a visit that happens to be attributable and
           // not a different destination. `startsWith`, since every other arm
           // here is an exact match and a prefix rule has to be explicit about
           // it. Deep paths reach React at all because vercel.json already
           // rewrites everything outside /api to index.html.
           : path === '/mobile/rf' || path.startsWith('/mobile/refer/') ? MobilePage
           // The NBSC 2026 scavenger hunt (docs/NBSC_HUNT.md). Case-insensitive,
           // because these are typed from a slide and printed on cards with
           // their capitals — and a phone keyboard may lowercase the lot.
           : path.toLowerCase() === '/nbscscavengerhunt' ? NBSCHuntPage
           : path.toLowerCase() === '/nbscscavengerhuntadmin' ? NBSCHuntAdminPage
           : path === '/umanitoba' ? PathfinderPage // campus pathfinder lives here now
           : LandingPage; // '/' and anything unmatched → support landing page

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <React.Suspense fallback={null}>
      <Page />
    </React.Suspense>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
